"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AskAIButton,
  OCRUploadCard,
  type OCRUploadedFile,
  type UserPromptRecord,
  useDebug,
  useForm,
  usePubSub,
} from "@huub-web/ui";
import type { WebSocketProgressStep } from "@huub-web/backend";
import { generateUuid } from "@huub-web/utils";

import { type AllowedFileTypes, getAllowedFileMimeTypes } from "../../utils";
import { useTemplates } from "../../hooks/useTemplates";
import { useProgressUpdate } from "../../hooks/useProgressUpdate";

export type OCRFormUploadTemplateProps = {
  /**
   * Unique identifier for this template instance
   */
  id: string;
  /**
   * Title text for the upload card
   */
  title?: string;
  /**
   * Description text for the upload card
   */
  description?: string;
  /**
   * Allowed file types
   */
  accepted?: AllowedFileTypes[];
  /**
   * Whether the upload is disabled
   */
  disabled?: boolean;
  /**
   * The endpoint to upload files to
   */
  endpoint: string;
  /**
   * Endpoint to view/download the uploaded document
   */
  viewEndpoint?: string;
  /**
   * Endpoint to delete the uploaded document
   */
  deleteEndpoint?: string;
  /**
   * Optional help prompt for the AI help button
   */
  helpPrompt?: UserPromptRecord;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Template for OCR form upload functionality.
 * Uses UploadCard for file selection and displays upload progress through
 * three stages: Upload, Analyse, and Data plaatsen.
 */
export const OCRFormUploadTemplate: FC<OCRFormUploadTemplateProps> = ({
  id,
  endpoint,
  title,
  description,
  accepted = ["pdf", "doc", "image"],
  disabled,
  viewEndpoint,
  deleteEndpoint,
  helpPrompt,
  ref,
}) => {
  const { t } = useTranslation();
  const {
    uploadDocument,
    deleteDocument,
    downloadDocument,
    lockUI,
    lockPrompts,
  } = useTemplates();
  const pubSub = usePubSub();
  const { log } = useDebug();
  const form = useForm();

  const [isHelpHovering, setIsHelpHovering] = useState(false);

  const handleHelpPrompt = (prompt?: UserPromptRecord) => {
    if (prompt && !lockPrompts) {
      pubSub.publish("sendPrompt", {
        value: prompt,
      });
    }
  };

  const [uploadedFiles, setUploadedFiles] = useState<OCRUploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [entityID, setEntityID] = useState<string | undefined>(undefined);

  const allowedTypes: string[] = getAllowedFileMimeTypes(accepted);

  /**
   * Handle file upload
   */
  const handleUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const fileID = generateUuid();

    log("purple", "OCRFormUploadTemplate", "handleUpload", file);

    // Add file with initial state (no steps yet, loading spinner shown)
    const newFile: OCRUploadedFile = {
      id: fileID,
      name: file.name,
      status: "in-progress",
    };

    setUploadedFiles([newFile]);
    setIsUploading(true);

    // Upload the file
    const response = await uploadDocument(endpoint, file);

    if (!response.success) {
      // Upload failed
      setUploadedFiles([
        {
          ...newFile,
          status: "error",
        },
      ]);
      setIsUploading(false);
      return;
    }

    // Upload succeeded, start listening for progress updates
    setEntityID(id);
    setIsUploading(false);
    // Keep file in-progress state, steps will be populated by PROGRESS_UPDATE
  };

  /**
   * Handle retry upload
   */
  const handleRetry = () => {
    // Clear the failed file and let user upload again
    setUploadedFiles([]);
    setEntityID(undefined);
  };

  /**
   * Handle view/download document
   */
  const handleView = async () => {
    if (!viewEndpoint) return;
    await downloadDocument(viewEndpoint);
  };

  /**
   * Handle delete document
   */
  const handleDelete = async (fileId: string) => {
    if (!deleteEndpoint) return;
    const response = await deleteDocument(deleteEndpoint, fileId);
    if (response.success) {
      setUploadedFiles([]);
      setEntityID(undefined);
    }
  };

  /**
   * Handles progress updates from WebSocket.
   * Passes steps directly from backend - backend controls what is shown.
   */
  const handleProgressUpdate = useCallback(
    (progressSteps: WebSocketProgressStep[]) => {
      log("purple", "OCRFormUploadTemplate", "progress update", progressSteps);

      setUploadedFiles((prev) => {
        if (prev.length === 0) return prev;

        const currentFile = prev[0];

        // Determine overall status from steps
        const hasError = progressSteps.some((s) => s.status === "error");
        const allCompleted = progressSteps.every(
          (s) => s.status === "completed",
        );

        let status: OCRUploadedFile["status"] = "in-progress";
        let errorMessage: string | undefined;

        if (hasError) {
          status = "error";
          const errorStep = progressSteps.find((s) => s.status === "error");
          errorMessage = errorStep?.message;
        } else if (allCompleted) {
          status = "success";
        }

        return [
          {
            ...currentFile,
            status,
            steps: progressSteps,
            errorMessage,
          },
        ];
      });
    },
    [log],
  );

  /**
   * Listen for WebSocket progress updates for this entity
   */
  const { progress } = useProgressUpdate({
    entityID,
    entity: "document-analysis",
    scope: "status",
  });

  /**
   * Update uploaded files when progress changes
   */
  useEffect(() => {
    if (progress.length > 0 && uploadedFiles.length > 0) {
      handleProgressUpdate(progress);
    }
  }, [progress, handleProgressUpdate]);

  /**
   * Determine if the UI is locked
   */
  const isLocked = useMemo(() => {
    const hasActiveAnalysis = uploadedFiles.some(
      (file) => file.status === "in-progress",
    );
    return lockUI || isUploading || hasActiveAnalysis;
  }, [lockUI, isUploading, uploadedFiles]);

  /**
   * Disable/enable the form based on lock state
   */
  useEffect(() => {
    if (!form) return;

    if (isLocked) {
      form.disable();
    } else {
      form.enable();
    }
  }, [isLocked, form]);

  return (
    <OCRUploadCard
      data-id={id}
      ref={ref}
      title={title}
      description={description ?? t("ocrFormUpload.description")}
      onUpload={handleUpload}
      acceptedTypes={allowedTypes}
      disabled={disabled || isLocked}
      loading={isUploading}
      uploadedFiles={uploadedFiles}
      onView={viewEndpoint ? handleView : undefined}
      onDelete={deleteEndpoint ? handleDelete : undefined}
      onRetry={handleRetry}
      disableTooltip={isHelpHovering}
      style={{ pointerEvents: isHelpHovering ? "none" : undefined }}
      trailing={
        helpPrompt && (
          <AskAIButton
            as="div"
            disabled={lockPrompts}
            style={{ pointerEvents: isHelpHovering ? "auto" : undefined }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleHelpPrompt(helpPrompt);
            }}
            onMouseEnter={() => setIsHelpHovering(true)}
            onMouseLeave={() => setIsHelpHovering(false)}
          />
        )
      }
    />
  );
};
